import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useTranslation } from 'react-i18next'
import { Loader2 } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Switch } from '@/components/ui/switch'
import type {
  EntraTrustedTenant,
  EntraTrustedTenantInput,
} from '@/lib/services/integrationService'

const formSchema = z.object({
  name: z.string().min(2).max(200),
  tenantId: z.string().min(2).max(255),
  description: z.string().max(1000).nullable().optional(),
  isActive: z.boolean(),
  allowRoleClaims: z.boolean(),
})

type FormValues = z.infer<typeof formSchema>

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSubmit: (payload: EntraTrustedTenantInput) => Promise<void>
  tenant?: EntraTrustedTenant | null
  mode: 'create' | 'edit'
}

export function EntraTrustedTenantDialog({
  open,
  onOpenChange,
  onSubmit,
  tenant,
  mode,
}: Props) {
  const { t } = useTranslation()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: '',
      tenantId: '',
      description: '',
      isActive: true,
      allowRoleClaims: true,
    },
  })

  useEffect(() => {
    if (!open) return

    if (tenant && mode === 'edit') {
      form.reset({
        name: tenant.name,
        tenantId: tenant.tenantId,
        description: tenant.description ?? '',
        isActive: tenant.isActive,
        allowRoleClaims: tenant.allowRoleClaims,
      })
      return
    }

    form.reset({
      name: '',
      tenantId: '',
      description: '',
      isActive: true,
      allowRoleClaims: true,
    })
  }, [form, mode, open, tenant])

  const handleSubmit = async (values: FormValues) => {
    setIsSubmitting(true)
    try {
      await onSubmit({
        name: values.name,
        tenantId: values.tenantId,
        description: values.description || null,
        isActive: values.isActive,
        allowRoleClaims: values.allowRoleClaims,
      })
      onOpenChange(false)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[560px]">
        <DialogHeader>
          <DialogTitle>
            {mode === 'create'
              ? t('integration.entra.addTrustedTenant', 'Vertrouwde tenant toevoegen')
              : t('integration.entra.editTrustedTenant', 'Vertrouwde tenant bewerken')}
          </DialogTitle>
          <DialogDescription>
            {t(
              'integration.entra.tenantDialogDescription',
              'Alleen tenants op deze whitelist mogen roles-claims gebruiken voor autorisatie.'
            )}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form className="space-y-4" onSubmit={form.handleSubmit(handleSubmit)}>
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('tenants.tenantName', 'Tenantnaam')}</FormLabel>
                  <FormControl>
                    <Input placeholder="VRH" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="tenantId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('tenants.tenantId', 'Tenant ID')}</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="00000000-0000-0000-0000-000000000000"
                      autoCapitalize="none"
                      autoCorrect="off"
                      spellCheck={false}
                      {...field}
                    />
                  </FormControl>
                  <FormDescription>
                    {t(
                      'integration.entra.tenantIdHelp',
                      'Exacte Entra tenant ID waarvoor roles-claims expliciet zijn toegestaan.'
                    )}
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('common.description', 'Beschrijving')}</FormLabel>
                  <FormControl>
                    <Textarea rows={3} {...field} value={field.value ?? ''} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="allowRoleClaims"
              render={({ field }) => (
                <FormItem className="flex items-center justify-between rounded-md border p-4">
                  <div className="space-y-1">
                    <FormLabel>{t('integration.entra.allowRoleClaims', 'Roles-claims toestaan')}</FormLabel>
                    <FormDescription>
                      {t(
                        'integration.entra.allowRoleClaimsHelp',
                        'Schakel uit om tenant wel zichtbaar te houden maar roles-claims te blokkeren.'
                      )}
                    </FormDescription>
                  </div>
                  <FormControl>
                    <Switch checked={field.value} onCheckedChange={field.onChange} />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="isActive"
              render={({ field }) => (
                <FormItem className="flex items-center justify-between rounded-md border p-4">
                  <div className="space-y-1">
                    <FormLabel>{t('integration.entra.enabled', 'Ingeschakeld')}</FormLabel>
                    <FormDescription>
                      {t(
                        'integration.entra.tenantEnabledHelp',
                        'Alleen ingeschakelde tenants mogen nog roles-claims gebruiken.'
                      )}
                    </FormDescription>
                  </div>
                  <FormControl>
                    <Switch checked={field.value} onCheckedChange={field.onChange} />
                  </FormControl>
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                {t('common.cancel', 'Annuleren')}
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                {mode === 'create'
                  ? t('integration.entra.createTenant', 'Toevoegen')
                  : t('integration.entra.saveTenant', 'Opslaan')}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
